import type { ParserMode } from "./types";

export const ACCEPTED_FILES = ".txt,.md,.fountain,.fdx,.docx,.pdf,text/plain,application/xml,text/xml,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export function parserModeForFile(name: string): Extract<ParserMode, "fountain" | "fdx"> | undefined {
  const extension = fileExtension(name);
  return extension === "fountain" || extension === "fdx" ? extension : undefined;
}

export async function textFromFile(file: File): Promise<string> {
  const extension = fileExtension(file.name);
  if (isTextFile(file, extension) || extension === "fountain" || extension === "fdx") return file.text();
  if (extension === "docx") return textFromDocx(file);
  if (extension === "pdf" || file.type === "application/pdf") return textFromPdf(file);
  throw new Error("Unsupported file. Choose a TXT, Markdown, Fountain, FDX, DOCX, or PDF document.");
}

function fileExtension(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

function isTextFile(file: File, extension: string): boolean {
  return file.type.startsWith("text/") || extension === "txt" || extension === "md";
}

async function textFromDocx(file: File): Promise<string> {
  const mammoth = await import("mammoth");
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
  return result.value;
}

async function textFromPdf(file: File): Promise<string> {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.mjs", import.meta.url).toString();
  const pdf = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages: string[] = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    pages.push(await textFromPdfPage(pdf, pageNumber));
  }
  return pages.join("\n\n");
}

async function textFromPdfPage(pdf: Awaited<ReturnType<(typeof import("pdfjs-dist"))["getDocument"]>["promise"]>, pageNumber: number): Promise<string> {
  const page = await pdf.getPage(pageNumber);
  const content = await page.getTextContent();
  const text = content.items.map((item) => "str" in item ? item.str : "").join(" ");
  return `#Page${pageNumber}\n${text}`;
}
